/**
 * PATH: packages/job-hub/src/jobs/qa/QaProductSearchField.tsx
 * FILE_ID: JOBHUB-QA-PRODUCT-FIELD-001
 * PURPOSE: The product cell, with pricelist name suggestions under it.
 * LANDMINE: this is a SUGGESTION field, not a picker. Free text must always
 *      commit — plenty of site materials are not in the pricelist — so the
 *      input never validates against the results and never blocks on them.
 *      Blur commits before the suggestion list can be clicked, hence the
 *      mousedown-not-click handler on each option.
 */
import { useEffect, useRef, useState } from 'react'
import { EditableCell } from './EditableCell'
import { searchQaProducts, type QaProductOption } from './qaProductSearch'
import type { JobHubHttpClient } from '../jobHubDataAdapter'

/** Long enough that a two-finger typist is not firing a request per letter. */
const DEBOUNCE_MS = 200
const MIN_CHARS = 2

export function QaProductSearchField({
  value,
  client,
  disabled = false,
  ariaLabel,
  placeholder = 'Product / material',
  onCommit,
}: {
  value: string
  client?: JobHubHttpClient
  disabled?: boolean
  ariaLabel: string
  placeholder?: string
  onCommit: (next: string) => void
}) {
  const [term, setTerm] = useState('')
  const [options, setOptions] = useState<QaProductOption[]>([])
  const [open, setOpen] = useState(false)
  const live = useRef(true)

  useEffect(() => {
    live.current = true
    return () => {
      live.current = false
    }
  }, [])

  useEffect(() => {
    if (!client || term.trim().length < MIN_CHARS) {
      setOptions([])
      return
    }
    const timer = setTimeout(() => {
      void searchQaProducts(client, term).then((rows) => {
        if (live.current) setOptions(rows)
      })
    }, DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [client, term])

  const choose = (name: string) => {
    setOpen(false)
    setOptions([])
    onCommit(name)
  }

  return (
    <div
      className="relative"
      onInput={(event) => {
        setTerm((event.target as HTMLTextAreaElement).value ?? '')
        setOpen(true)
      }}
      onBlur={() => setOpen(false)}
    >
      <EditableCell
        value={value}
        singleLine
        disabled={disabled}
        ariaLabel={ariaLabel}
        placeholder={placeholder}
        onCommit={onCommit}
      />
      {open && options.length > 0 && (
        <ul
          role="listbox"
          aria-label="Pricelist product suggestions"
          className="absolute left-0 right-0 top-full z-20 mt-0.5 max-h-48 overflow-auto rounded-md border border-border bg-popover py-1 shadow-lg"
        >
          {options.map((option) => (
            <li key={option.id || option.name}>
              <button
                type="button"
                role="option"
                aria-selected={false}
                // mousedown, not click: blur fires first and would close the
                // list before a click ever landed.
                onMouseDown={(event) => {
                  event.preventDefault()
                  choose(option.name)
                }}
                className="block w-full truncate px-2 py-1.5 text-left text-sm hover:bg-accent"
              >
                {option.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
